import apiClient, { BASE_URL } from './client';

// Announcements
export interface Announcement {
  id: string;
  title: string;
  body: string;
  category: string;
  priority: string;
  published_at: string;
  expires_at: string | null;
  author_id: string;
  author_name?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface ApiOk<T> { success: true; data: T }

/** Active announcements newest first. Unwraps the standard `{ success, data }` envelope. */
export const getAnnouncements = async (): Promise<Announcement[]> => {
  const r = await apiClient.get('/api/v1/announcements');
  const body = r.data as ApiOk<Announcement[]> | Announcement[];
  if (Array.isArray(body)) return body;
  return body.data ?? [];
};

// ── Home Page Expansion (Carolyn 2026-05-07, E2-E6) ─────────────────────────

function unwrapList<T>(body: unknown): T[] {
  if (Array.isArray(body)) return body as T[];
  const ok = body as ApiOk<T[]> | { data?: T[] };
  return ok.data ?? [];
}
function unwrapOne<T>(body: unknown): T | null {
  if (body == null) return null;
  if (typeof body === 'object' && 'success' in (body as object)) {
    return (body as ApiOk<T | null>).data ?? null;
  }
  return body as T;
}

/** E2 — Company calendar. */
export interface CompanyEvent {
  id: string;
  title: string;
  description: string;
  location: string | null;
  starts_at: string;
  ends_at: string | null;
  all_day: boolean;
  category: string;
  link: string | null;
  is_active: boolean;
  created_by: string;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}
export const getUpcomingEvents = async (): Promise<CompanyEvent[]> => {
  const r = await apiClient.get('/api/v1/events', { params: { upcoming: 'true' } });
  return unwrapList<CompanyEvent>(r.data);
};
/** Admin: full list including past/inactive. */
export const listAllEvents = async (): Promise<CompanyEvent[]> => {
  const r = await apiClient.get('/api/v1/events', { params: { upcoming: 'false', include_inactive: 'true' } });
  return unwrapList<CompanyEvent>(r.data);
};
export const createEvent = async (body: Partial<CompanyEvent>): Promise<CompanyEvent> => {
  const r = await apiClient.post('/api/v1/events', body);
  return unwrapOne<CompanyEvent>(r.data) as CompanyEvent;
};
export const updateEvent = async (id: string, body: Partial<CompanyEvent>): Promise<CompanyEvent> => {
  const r = await apiClient.put(`/api/v1/events/${id}`, body);
  return unwrapOne<CompanyEvent>(r.data) as CompanyEvent;
};
export const deleteEvent = async (id: string): Promise<void> => { await apiClient.delete(`/api/v1/events/${id}`); };

/** E3 — Employee celebrations. */
export interface EmployeeMilestone {
  id: string;
  person_id: string;
  person_name: string;
  person_email: string;
  person_job_title: string | null;
  type: 'birthday' | 'anniversary' | 'milestone';
  date: string;
  label: string | null;
  is_active: boolean;
}
export interface UpcomingMilestone extends EmployeeMilestone {
  upcoming_date: string;
  years: number;
}
export const getUpcomingMilestones = async (days = 30): Promise<UpcomingMilestone[]> => {
  const r = await apiClient.get('/api/v1/employee-milestones/upcoming', { params: { days } });
  return unwrapList<UpcomingMilestone>(r.data);
};
/** Admin: full milestone list (active + inactive). */
export const listAllMilestones = async (): Promise<EmployeeMilestone[]> => {
  const r = await apiClient.get('/api/v1/employee-milestones', { params: { include_inactive: 'true' } });
  return unwrapList<EmployeeMilestone>(r.data);
};
export const upsertMilestone = async (body: {
  person_id: string;
  person_name: string;
  person_email: string;
  person_job_title?: string | null;
  type: 'birthday' | 'anniversary' | 'milestone';
  date: string;
  label?: string | null;
}): Promise<EmployeeMilestone> => {
  const r = await apiClient.post('/api/v1/employee-milestones', body);
  return unwrapOne<EmployeeMilestone>(r.data) as EmployeeMilestone;
};
export const deleteMilestone = async (id: string): Promise<void> => {
  await apiClient.delete(`/api/v1/employee-milestones/${id}`);
};

/** E4 — Newsletter / Chronicle. */
export interface ChroniclePost {
  id: string;
  title: string;
  slug: string;
  summary: string;
  body: string;
  hero_image_url: string | null;
  author_id: string;
  author_name: string;
  is_published: boolean;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}
export const getChroniclePosts = async (): Promise<ChroniclePost[]> => {
  const r = await apiClient.get('/api/v1/chronicle');
  return unwrapList<ChroniclePost>(r.data);
};
/** Admin: include drafts. */
export const listAllChroniclePosts = async (): Promise<ChroniclePost[]> => {
  const r = await apiClient.get('/api/v1/chronicle', { params: { include_drafts: 'true' } });
  return unwrapList<ChroniclePost>(r.data);
};
export const getChroniclePost = async (slug: string): Promise<ChroniclePost> => {
  const r = await apiClient.get(`/api/v1/chronicle/${encodeURIComponent(slug)}`);
  return unwrapOne<ChroniclePost>(r.data) as ChroniclePost;
};
export const createChroniclePost = async (body: Partial<ChroniclePost>): Promise<ChroniclePost> => {
  const r = await apiClient.post('/api/v1/chronicle', body);
  return unwrapOne<ChroniclePost>(r.data) as ChroniclePost;
};
export const updateChroniclePost = async (id: string, body: Partial<ChroniclePost>): Promise<ChroniclePost> => {
  const r = await apiClient.put(`/api/v1/chronicle/${id}`, body);
  return unwrapOne<ChroniclePost>(r.data) as ChroniclePost;
};
export const deleteChroniclePost = async (id: string): Promise<void> => {
  await apiClient.delete(`/api/v1/chronicle/${id}`);
};

/** E5 — Employee highlights / nominations. */
export interface EmployeeHighlight {
  id: string;
  nominee_id: string;
  nominee_name: string;
  nominee_email: string;
  nominee_job_title: string | null;
  nominator_id: string;
  nominator_name: string;
  nominator_email: string;
  reason: string;
  category: string | null;
  status: 'nominated' | 'approved' | 'declined' | 'featured';
  featured_at: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  created_at: string;
  updated_at: string;
}
export const getFeaturedHighlight = async (): Promise<EmployeeHighlight | null> => {
  const r = await apiClient.get('/api/v1/employee-highlights/featured');
  return unwrapOne<EmployeeHighlight>(r.data);
};
export const submitHighlightNomination = async (body: {
  nominee_id: string;
  nominee_name: string;
  nominee_email: string;
  nominee_job_title?: string | null;
  reason: string;
  category?: string | null;
}): Promise<EmployeeHighlight> => {
  const r = await apiClient.post('/api/v1/employee-highlights', body);
  return unwrapOne<EmployeeHighlight>(r.data) as EmployeeHighlight;
};
/** Admin queue (all highlights regardless of status). */
export const getHighlightQueue = async (): Promise<EmployeeHighlight[]> => {
  const r = await apiClient.get('/api/v1/employee-highlights/queue');
  return unwrapList<EmployeeHighlight>(r.data);
};
export const updateHighlightStatus = async (
  id: string,
  body: { status?: EmployeeHighlight['status']; review_notes?: string | null; category?: string | null },
): Promise<EmployeeHighlight> => {
  const r = await apiClient.patch(`/api/v1/employee-highlights/${id}`, body);
  return unwrapOne<EmployeeHighlight>(r.data) as EmployeeHighlight;
};
export const deleteHighlight = async (id: string): Promise<void> => {
  await apiClient.delete(`/api/v1/employee-highlights/${id}`);
};

/** E6 — Coworker updates feed. */
export interface CoworkerUpdate {
  id: string;
  type: 'new_hire' | 'promotion' | 'role_change' | 'team_change' | 'termination' | 'milestone';
  person_id: string;
  person_name: string;
  person_email: string | null;
  person_job_title: string | null;
  person_location: string | null;
  headline: string;
  narrative: string | null;
  effective_date: string;
  is_active: boolean;
  posted_by: string;
  posted_by_name: string | null;
  posted_at: string;
}
export const getCoworkerUpdates = async (limit = 30): Promise<CoworkerUpdate[]> => {
  const r = await apiClient.get('/api/v1/coworker-updates', { params: { limit } });
  return unwrapList<CoworkerUpdate>(r.data);
};
/** Admin: list including inactive. */
export const listAllCoworkerUpdates = async (): Promise<CoworkerUpdate[]> => {
  const r = await apiClient.get('/api/v1/coworker-updates', { params: { include_inactive: 'true', limit: 200 } });
  return unwrapList<CoworkerUpdate>(r.data);
};
export const createCoworkerUpdate = async (body: Partial<CoworkerUpdate>): Promise<CoworkerUpdate> => {
  const r = await apiClient.post('/api/v1/coworker-updates', body);
  return unwrapOne<CoworkerUpdate>(r.data) as CoworkerUpdate;
};
export const updateCoworkerUpdate = async (id: string, body: Partial<CoworkerUpdate>): Promise<CoworkerUpdate> => {
  const r = await apiClient.put(`/api/v1/coworker-updates/${id}`, body);
  return unwrapOne<CoworkerUpdate>(r.data) as CoworkerUpdate;
};
export const deleteCoworkerUpdate = async (id: string): Promise<void> => {
  await apiClient.delete(`/api/v1/coworker-updates/${id}`);
};

// Strategy
export const getStrategy = async () =>
  apiClient.get('/api/v1/strategy').then(r => r.data as unknown);

// Town Halls
export const getTownHalls = async () =>
  apiClient.get('/api/v1/town-halls').then(r => r.data as unknown);

// Executives
export const getExecutives = async () =>
  apiClient.get('/api/v1/executives').then(r => r.data as unknown);

// CEO Message
export interface CEOMessage {
  id: string;
  title: string;
  body: string;
  video_url: string | null;
  signature_name: string;
  signature_title: string;
  is_published: boolean;
  published_at: string;
  updated_by: string;
  updated_by_name: string;
  updated_at: string;
}
export const getCEOMessage = async () =>
  apiClient.get('/api/v1/ceo-message').then(r => r.data as CEOMessage | null);
export const getCEOMessageHistory = async () =>
  apiClient.get('/api/v1/ceo-message/all').then(r => r.data as CEOMessage[]);
export const createCEOMessage = async (data: {
  title: string;
  body: string;
  video_url?: string | null;
  signature_name: string;
  signature_title: string;
  is_published?: boolean;
}) =>
  apiClient.post('/api/v1/ceo-message', data).then(r => r.data as CEOMessage);
export const updateCEOMessage = async (id: string, data: Partial<{
  title: string;
  body: string;
  video_url: string | null;
  signature_name: string;
  signature_title: string;
  is_published: boolean;
}>) =>
  apiClient.put(`/api/v1/ceo-message/${id}`, data).then(r => r.data as CEOMessage);
export const deleteCEOMessage = async (id: string) =>
  apiClient.delete(`/api/v1/ceo-message/${id}`).then(r => r.data as { id: string });

// Ask Andrew
export const getAskAndrew = async (params?: { status?: string; category?: string }) =>
  apiClient.get('/api/v1/ask-andrew', { params }).then(r => r.data as unknown);
export const submitQuestion = async (data: { question: string; category?: string; is_anonymous?: boolean }) =>
  apiClient.post('/api/v1/ask-andrew', data).then(r => r.data as unknown);

// KPIs
export const getKPIs = async (params?: Record<string, string>) =>
  apiClient.get('/api/v1/kpis', { params }).then(r => r.data as unknown);
export const getKPI = async (id: string) =>
  apiClient.get(`/api/v1/kpis/${id}`).then(r => r.data as unknown);
export const createKPI = async (data: Record<string, unknown>) =>
  apiClient.post('/api/v1/kpis', data).then(r => r.data as unknown);
export const updateKPI = async (id: string, data: Record<string, unknown>) =>
  apiClient.put(`/api/v1/kpis/${id}`, data).then(r => r.data as unknown);
export const submitCheckin = async (kpiId: string, data: { reported_value: number; notes?: string }) =>
  apiClient.post(`/api/v1/kpis/${kpiId}/checkin`, data).then(r => r.data as unknown);
export const getKPICheckins = async (kpiId: string) =>
  apiClient.get(`/api/v1/kpis/${kpiId}/checkins`).then(r => r.data as unknown);
export const getKPIReportSummary = async (params?: Record<string, string>) =>
  apiClient.get('/api/v1/kpis/reports/summary', { params }).then(r => r.data as unknown);

// Documents
export const getDocuments = async (params?: Record<string, string>) =>
  apiClient.get('/api/v1/documents', { params }).then(r => r.data as unknown);
export const getDocumentCategories = async () =>
  apiClient.get('/api/v1/documents/categories').then(r => r.data as unknown);

// Atlas AI
export const chatWithAtlasAI = async (message: string, history?: Array<{role: string; content: string}>) =>
  apiClient.post('/api/v1/atlas-ai/chat', { message, conversationHistory: history }).then(r => r.data as unknown);

/**
 * Streaming chat. Returns an AbortController so the caller can cancel mid-stream
 * (e.g. user closes the panel). The callbacks fire as SSE events arrive:
 *   - onSources: sources list (fires once near the start)
 *   - onDelta:   one text chunk; append to current assistant message
 *   - onDone:    stream complete
 *   - onError:   any failure (auth, network, model)
 *
 * Arul 2026-05-11 — Built to give Atlas AI a "Claude-fast" feel. Time-to-first-
 * token drops from full-roundtrip to a few hundred ms; the user sees text
 * stream live instead of waiting for a complete response.
 */
export function streamAtlasAI(
  message: string,
  history: Array<{ role: string; content: string }> | undefined,
  callbacks: {
    onSources?: (sources: string[]) => void;
    onDelta: (chunk: string) => void;
    onDone: () => void;
    onError: (err: Error) => void;
  },
): AbortController {
  const controller = new AbortController();
  const baseURL = (apiClient.defaults.baseURL ?? '').replace(/\/$/, '');

  (async () => {
    try {
      // Acquire the bearer token via the same MSAL helper the axios
      // interceptor uses. If it fails we let the request go without a
      // token; the API will 401 and the global response interceptor
      // handles re-login.
      let bearer = '';
      try {
        const { acquireToken } = await import('@/auth/msalConfig');
        bearer = await acquireToken();
      } catch (e) {
        console.warn('Atlas AI stream: token acquisition failed', e);
      }

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
      };

      const resp = await fetch(`${baseURL}/api/v1/atlas-ai/chat/stream`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ message, conversationHistory: history }),
        signal: controller.signal,
        credentials: 'include',
      });

      if (!resp.ok || !resp.body) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Stream failed: ${resp.status} ${text}`.trim());
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';

      // SSE framing: events end at `\n\n`. Each event has lines like
      //   event: delta
      //   data: <payload>
      // We accumulate `buf` and split off complete events as they arrive.
      let streamDone = false;
      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) { streamDone = true; break; }
        buf += decoder.decode(value, { stream: true });
        let idx = buf.indexOf('\n\n');
        while (idx !== -1) {
          const raw = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          idx = buf.indexOf('\n\n');
          let eventName = 'message';
          const dataLines: string[] = [];
          for (const line of raw.split('\n')) {
            if (line.startsWith('event:')) eventName = line.slice(6).trim();
            else if (line.startsWith('data:')) {
              // SSE spec: "data:<optional single space><payload>". Strip
              // ONLY that single optional framing space — never call
              // .trimStart() here. Arul 2026-05-11 — the previous
              // .trimStart() was eating the LEADING SPACE of every
              // token in a streamed response, jamming words together
              // (e.g. "Based on what" rendered as "Basedon what",
              // "yet. RM" as "yet.RM"). Critical fix.
              let payload = line.slice(5);
              if (payload.startsWith(' ')) payload = payload.slice(1);
              dataLines.push(payload);
            }
          }
          const dataStr = dataLines.join('\n');
          if (!dataStr) continue;

          if (eventName === 'delta') {
            // delta payload is a JSON string (the server JSON.stringify's it)
            try {
              const text = JSON.parse(dataStr) as string;
              callbacks.onDelta(text);
            } catch {
              callbacks.onDelta(dataStr);
            }
          } else if (eventName === 'sources') {
            try {
              const list = JSON.parse(dataStr) as string[];
              callbacks.onSources?.(list);
            } catch { /* ignore malformed sources frame */ }
          } else if (eventName === 'done') {
            callbacks.onDone();
            return;
          } else if (eventName === 'error') {
            try {
              const { message: msg } = JSON.parse(dataStr) as { message?: string };
              throw new Error(msg ?? 'Stream error');
            } catch (e) {
              throw e instanceof Error ? e : new Error(String(e));
            }
          }
        }
      }
      callbacks.onDone();
    } catch (err) {
      // AbortError is the user closing the panel — quiet.
      if (err instanceof Error && err.name === 'AbortError') return;
      callbacks.onError(err instanceof Error ? err : new Error(String(err)));
    }
  })();

  return controller;
}

// FAQs (Ask Andrew archive)
export const getFAQs = async (params?: { search?: string; category?: string }) =>
  apiClient.get('/api/v1/faqs', { params }).then(r => r.data as unknown);
export const getFAQCategories = async () =>
  apiClient.get('/api/v1/faqs/categories').then(r => r.data as unknown);
export interface FAQUploadProgress {
  stage: 'received' | 'extracting' | 'extracted' | 'syncing' | 'complete' | 'error';
  message: string;
  percent: number;
  count?: number;
  batch_id?: string;
  code?: string;
}

/**
 * Upload a FAQ PDF using a two-phase approach:
 *   1. POST the file → returns a jobId immediately (202)
 *   2. Poll GET /upload-status/:jobId every 5s using raw fetch (simple, no interceptors)
 */
export async function uploadFAQPdf(
  file: File,
  onProgress?: (p: FAQUploadProgress) => void,
): Promise<{ count: number }> {
  // Phase 1: Upload the file via apiClient — returns instantly with a jobId.
  // NOTE (2026-05-07): do NOT set Content-Type explicitly. When the body is a
  // FormData the browser/axios auto-generates the boundary string in the
  // header (`multipart/form-data; boundary=----abc...`). Forcing the bare
  // `multipart/form-data` value strips the boundary and multer can't parse
  // the request — this was the Ask Andrew PDF upload bug Carolyn reported.
  const form = new FormData();
  form.append('file', file);
  const uploadResp = await apiClient.post('/api/v1/faqs/upload', form, {
    timeout: 60_000,
  });
  const respData = uploadResp.data as Record<string, unknown>;
  const jobId = respData?.jobId as string | undefined;
  if (!jobId) throw new Error('Upload failed — no job ID returned.');

  // Phase 2: Poll for status with raw fetch — no interceptors, no caching issues
  const { acquireToken } = await import('@/auth/msalConfig');
  const maxWaitMs = 15 * 60 * 1000; // 15 min — large PDFs with rate-limit retries can take a while
  const startTime = Date.now();

  while (Date.now() - startTime < maxWaitMs) {
    await new Promise(r => setTimeout(r, 5_000));

    try {
      const token = await acquireToken();
      const url = `${BASE_URL}/api/v1/faqs/upload-status/${jobId}`;
      const resp = await fetch(url, {
        headers: { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache' },
      });

      if (!resp.ok) continue; // transient error — keep polling

      const body = await resp.json() as { success?: boolean; data?: FAQUploadProgress };
      const data = body?.data ?? body as unknown as FAQUploadProgress;
      if (!data?.stage) continue;

      onProgress?.(data);
      if (data.stage === 'complete') return { count: data.count ?? 0 };
      if (data.stage === 'error') throw new Error(data.message || 'Import failed.');
    } catch (err) {
      if (err instanceof Error && err.message.includes('Import failed')) throw err;
    }
  }

  // Timeout — but the backend may have completed. Check if FAQs exist.
  try {
    const faqResp = await apiClient.get('/api/v1/faqs');
    const faqs = faqResp.data as Array<{ is_new?: boolean }>;
    const newCount = Array.isArray(faqs) ? faqs.filter(f => f.is_new).length : 0;
    if (newCount > 0) return { count: newCount }; // Backend completed — success!
  } catch { /* ignore */ }

  throw new Error('Upload is still processing. Refresh the page in a minute to check.');
}
export const promoteFAQBatch = async () =>
  apiClient.post('/api/v1/faqs/promote').then(r => r.data as unknown);

// Admin
export const getAdminStats = async () =>
  apiClient.get('/api/v1/admin/stats').then(r => r.data as unknown);

export const getAdminAnnouncements = async () =>
  apiClient.get('/api/v1/admin/announcements').then(r => r.data as unknown);
